import { Link } from 'react-router-dom'
import { flowUrl, messageType, type FlowKind } from '../api/flows'
import { useKratosFlow } from '../hooks/useKratosFlow'
import { AuthLayout, AuthLoading } from './AuthLayout'
import { ErrorMessage } from './ErrorMessage'
import { KratosForm } from './KratosForm'
import { PasskeyForm } from './PasskeyForm'
import { useI18n } from '../i18n'
import { translateKratosMessage } from '../i18n/kratos'

const copy: Record<FlowKind, { title: string; description: string; submit: string }> = {
  login: { title: 'Sign in', description: 'Continue with your TOGAPhotos account.', submit: 'Sign in' },
  registration: { title: 'Create your account', description: 'We will send a code to verify your email address.', submit: 'Create account' },
  recovery: { title: 'Reset your password', description: 'Enter your email address to receive a recovery code.', submit: 'Continue' },
  verification: { title: 'Verify your email', description: 'Enter the code from your email, or request a new one.', submit: 'Verify email' },
  settings: { title: 'Account settings', description: 'Update your password and account information.', submit: 'Save changes' },
}

const flowGroups: Record<FlowKind, string[]> = {
  login: ['password', 'passkey'], registration: ['password'], settings: ['password', 'profile', 'passkey'],
  recovery: ['code', 'link'], verification: ['code', 'link'],
}
const groupTitles: Record<string, string> = { password: 'Password', profile: 'Profile', passkey: 'Passkeys' }

export function AuthFlowPage({ kind }: { kind: FlowKind; home?: boolean }) {
  const { t } = useI18n()
  const state = useKratosFlow(kind)
  const { flow, loading, busy, error, success, context, version } = state
  const content = copy[kind]
  const availableGroups = flowGroups[kind].filter(group => flow?.ui.nodes.some(node => node.group === group))
  // A ?flow= callback can carry its SSO continuation only in the pending
  // response. Do not let early navigation discard that context.
  const accountLink = (to: string, label: string) => {
    if (loading || busy) return <span className="auth-link" role="link" aria-disabled="true">{label}</span>
    return <Link to={to} className="auth-link">{label}</Link>
  }

  return (
    <AuthLayout title={t(content.title)} description={t(context.refresh && kind === 'login'
      ? 'Please sign in again to continue.' : content.description)}>
      <div className="auth-form-area" aria-busy={loading || busy}>
        {loading ? <>
          <div className="auth-skeleton" aria-hidden="true"><span /><span /><span /></div>
          <AuthLoading message={kind === 'login' ? undefined : 'Preparing your form…'} />
        </> : <>
          {error && <ErrorMessage message={t(error)} className="mb-4" />}
          {success && <ErrorMessage message={t(success)} type="success" className="mb-4" />}
          {flow?.ui.messages?.map((message, index) => (
            <ErrorMessage key={`${message.id}-${index}`} message={translateKratosMessage(message, t)} type={messageType(message.type)} className="mb-4" />
          ))}
          {flow && availableGroups.map((group, index) => (
            <section key={group} className={index ? 'mt-8 pt-6 border-t border-gray-200' : undefined}>
              {kind === 'settings' && <h2 className="text-base font-semibold mb-4">{t(groupTitles[group])}</h2>}
              {group === 'passkey' && (kind === 'login' || kind === 'settings')
                ? <PasskeyForm key={`${flow.id}-${group}-${version}`} flow={flow} kind={kind} busy={busy} onSubmit={state.submit} />
                : <KratosForm key={`${flow.id}-${group}-${version}`} flow={flow} group={group} kind={kind} busy={busy}
                  submitLabel={t(content.submit)} onSubmit={state.submit} />}
            </section>
          ))}
          {!flow && <button type="button" onClick={state.restart} className="w-full btn-primary">{t('Try again')}</button>}
          {flow && availableGroups.length === 0 && <>
            <p className="text-sm text-gray-600 mb-4">{t('No sign-in method is available. Please start again.')}</p>
            <button type="button" onClick={state.restart} className="w-full btn-secondary">{t('Start again')}</button>
          </>}
        </>}
      </div>
      <nav className="auth-links" aria-label={t('Account help')}>
        {kind === 'login' && <>
          {!context.refresh && <p>{t('New to TOGAPhotos?')} {accountLink(flowUrl('registration', context), t('Create an account'))}</p>}
          <div className="auth-link-row">
            {accountLink(flowUrl('recovery', context), t('Forgot password?'))}
            {accountLink(flowUrl('verification', context), t('Verify email'))}
          </div>
        </>}
        {kind === 'registration' && <p>{t('Already have an account?')} {accountLink(flowUrl('login', { ...context, refresh: false }), t('Sign in'))}</p>}
        {(kind === 'recovery' || kind === 'verification') && accountLink(flowUrl('login', { ...context, refresh: false }), t('Back to sign in'))}
        {kind === 'settings' && accountLink(context.loginChallenge || context.returnTo ? flowUrl('login', { ...context, refresh: false }) : '/', t('Continue'))}
        {context.refresh && kind === 'login' && accountLink('/logout', t('Sign out and use another account'))}
      </nav>
    </AuthLayout>
  )
}
